/**
 * Deep-link `defaultSection` -> Settings sub-tab mapping (PRD §2.3).
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global). The table
 * of (skipReason -> Advanced Settings target). The underlying Advanced
 * Settings dialog in this monorepo is the Iris-Connection tabbed
 * surface — NOT the section-router the pre-monorepo reference PR
 * (`tryiris-ai/studio.client#1100`) assumed.
 * Per `stay-in-scope` (ai.knowledge/global): we adopt the EXISTING
 * `defaultTab` deep-link channel (see
 * `iris-connection-section.tsx::defaultTab`,
 * `chip-click.ts::chipClickOptions`, and `useSettings(device, options)`
 * in `room/components/video/hooks.ts`) instead of inventing a parallel
 * router-state pipeline. Same target landing area, same one-prop
 * surface — just different upstream callers (the bulk-add chip /
 * notification panel row instead of the per-tile chip).
 *
 * Mapping table:
 *
 *   skipReason                  | section label             | actual tab id
 *   ----------------------------|---------------------------|---------------
 *   auth                        | Authentication            | 'authentication'
 *   video-failed                | Video                     | 'video'
 *   control-failed              | Control                   | 'control'
 *   no-controls                 | Configure control         | 'control'
 *   missing-model               | Select driver             | 'driver'
 *   bridge-disambiguation       | Configure bridge          | 'monitoring' *
 *
 * Note: `video-failed` and `control-failed` are the post-add chip
 * buckets surfaced when a multi-added device's video or control
 * transport hits CONNECTION_STATUS_FAILED. They mirror the
 * `ChipState.targetTab` cascade from `chip-state.ts` ('video' /
 * 'control') and route to the same Advanced Settings sub-tabs. The
 * `no-controls` skipReason and the `control-failed` bucket both land
 * on the Control tab — `no-controls` covers the no-PTZ-configured case
 * (control aspect missing entirely), while `control-failed` covers the
 * control aspect existing but its connection driver erroring out. Both
 * deep-link to the Control tab where the user can configure / retry.
 *
 * (*) There is no dedicated "Configure bridge" sub-tab in this codebase
 *     — bridge status / disambiguation surfaces in the Monitoring tab
 *     (see `monitoring-content.tsx::MonitoringLite`). Routing
 *     bridge-disambiguation there lands the user on the surface that
 *     ALREADY shows the bridge-state UI; if Product later requests a
 *     dedicated bridge tab, this single mapping flips with no other
 *     change. Documented here so QA + future PRD revisions know why
 *     "Configure bridge" doesn't appear in the available-tabs list.
 *
 * Returns `undefined` for skipReasons that don't have a deep-link
 * target ("locked" / "already-linked" / "discovery-stale" /
 * "slot-trimmed" / "commit-rejected" / "error"). The caller falls back
 * to opening Advanced Settings at the default tab (no deep-link),
 * which is the safe behavior: if `defaultSection` value is unknown
 * (forward-compat), Advanced Settings opens at default top — no
 * error/blank-state.
 *
 * Per `think-in-systems` (ai.knowledge/global): pure-function mapper.
 * Zero React, zero router coupling — testable in isolation.
 */
import type { IrisConnectionTab } from '../iris-connection/iris-connection-section.js';
import type { MultiAddSkipReason } from '#~/notifications/types.js';

/**
 * Convert a `MultiAddSkipReason` into the corresponding Iris-Connection
 * sub-tab. Returns `undefined` when the reason has no dedicated tab
 * (the caller should open Settings at its default tab).
 *
 * @param skipReason - The reason a device was skipped or failed in the
 *   bulk-add pipeline (see `notifications/types.ts::MultiAddSkipReason`
 *   for the canonical list).
 * @returns The `IrisConnectionTab` to deep-link into, or `undefined`
 *   when no specific tab applies.
 */
export const skipReasonToDefaultTab = (
  skipReason: MultiAddSkipReason | null | undefined
): IrisConnectionTab | undefined => {
  if (!skipReason) return undefined;
  switch (skipReason) {
    case 'auth':
      // Auth failures land on the Authentication sub-tab, where the
      // user can update credentials and re-submit.
      return 'authentication';
    case 'video-failed':
      // Video CONNECTION_STATUS_FAILED bucket maps to the Video sub-
      // tab where transport / URI / RTSP settings can be reviewed.
      // Mirrors `ChipState.targetTab === 'video'` from `chip-state.ts`.
      return 'video';
    case 'control-failed':
      // Control (movement) CONNECTION_STATUS_FAILED bucket maps to
      // the Control sub-tab. Distinct from `no-controls` (which is the
      // missing-aspect case) — `control-failed` means the control
      // aspect exists but its driver couldn't connect.
      return 'control';
    case 'no-controls':
      // "Configure control" sub-tab. In this codebase the Iris-
      // Connection panel hosts a Control tab that handles ONVIF /
      // HTTP / VISCA setup.
      return 'control';
    case 'missing-model':
      // "Select driver". The Driver sub-tab is where the user picks
      // the make/model from the device registry — same target as the
      // tile chip's `'Select model'` action verb.
      return 'driver';
    case 'bridge-disambiguation':
      // "Configure bridge". This codebase has no dedicated bridge
      // sub-tab; the Monitoring tab surfaces bridge connection state
      // via MonitoringLite. Route here as the closest match. See
      // file-level comment for why this is a single-line flip if a
      // dedicated tab later lands.
      return 'monitoring';
    case 'locked':
    case 'already-linked':
    case 'discovery-stale':
    case 'slot-trimmed':
    case 'commit-rejected':
    case 'error':
    default:
      // Forward-compat — unknown / unrouted reasons open at the
      // default tab (no deep-link). Returning undefined keeps the
      // option object spreadable without a literal
      // `defaultTab: undefined` (exactOptionalPropertyTypes-safe).
      return undefined;
  }
};
